import { Panel, Loader, Stat, HStack } from "rsuite";
import { useEffect, lazy, Suspense } from "react";
import CanvasJSReactModule from "@canvasjs/react-charts";
import { useLandingService } from "./contexts/landing.service.context";
import EmptyState from "../../components/empty-state";
import RelaxImage from "../../assets/images/relax.png";
import NoIssuesImage from "../../assets/images/no-issues.png";
import "./index.css";

const CanvasJS = CanvasJSReactModule.CanvasJS;

const CanvasJSChart = lazy(() => import("@canvasjs/react-charts").then((module) => ({ default: module.default.CanvasJSChart })));

function LandingPage() {
  const {
    issuesChartOptions,
    passedPercentChartOptions,
    colors,
    isEmailsLoading,
    isIssuesLoading,
    isGrandTotalsLoading,
    error,
    emails,
    issues,
    grandTotals,
  } = useLandingService();

  useEffect(() => {
    if (CanvasJS && typeof CanvasJS.addColorSet === "function") {
      CanvasJS.addColorSet("customColorSet", colors);
    }
    return () => {
      if (CanvasJS && typeof CanvasJS.removeColorSet === "function") {
        CanvasJS.removeColorSet("customColorSet");
      }
    };
  }, [colors]);

  // Check if there's data for the charts
  const hasIssuesData = issues && issues.statusCounts && Object.keys(issues.statusCounts).length > 0;
  const hasEmailsData = emails && emails.statusCounts && Object.keys(emails.statusCounts).length > 0;

  if (error) {
    return (
      <div className="landing-container">
        <Panel header="Error" bordered shaded className="landing-panel">
          <p>Error loading data: {error}</p>
        </Panel>
      </div>
    );
  }

  return (
    <div className="landing-container">
      <Panel header="Child Record Issues" bordered shaded className="landing-panel" style={{ position: "relative" }}>
        <Suspense fallback={<div data-testid="loader">Loading Chart...</div>}>
          <div style={{ position: "relative", minHeight: 300 }}>
            {isIssuesLoading && (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.2)",
                  zIndex: 2,
                }}
              >
                <Loader size="lg" content="Loading Chart..." />
              </div>
            )}
            <div style={{ opacity: isIssuesLoading ? 0.3 : 1, height: "100%" }}>
              {!isIssuesLoading && !hasIssuesData ? (
                <EmptyState title="No Issues" message="No child records have issues!" image={NoIssuesImage} imageClassName="no-issues" />
              ) : (
                <div
                  className="canvas-container"
                  style={{ paddingLeft: 20, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <CanvasJSChart options={issuesChartOptions} className="custom-chart" />
                </div>
              )}
            </div>
          </div>
        </Suspense>
      </Panel>
      <Panel header="Emails Processed" bordered shaded className="landing-panel" style={{ position: "relative", width: "100%" }}>
        <Suspense
          fallback={
            <div data-testid="loader" style={{ width: "100%" }}>
              Loading Chart...
            </div>
          }
        >
          <div style={{ position: "absolute", minHeight: 300, width: "100%", bottom: 0 }}>
            {isEmailsLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.2)",
                  zIndex: 2,
                }}
              >
                <Loader size="lg" content="Loading Chart..." />
              </div>
            )}
            <div style={{ opacity: isEmailsLoading ? 0.3 : 1, height: "100%" }}>
              {!isEmailsLoading && !hasEmailsData ? (
                <EmptyState title="Nothing to do" message="All emails are processed and exported!" image={RelaxImage} imageClassName="relax" />
              ) : (
                <div
                  className="canvas-container"
                  style={{ paddingLeft: 20, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <CanvasJSChart options={passedPercentChartOptions} />
                </div>
              )}
            </div>
          </div>
        </Suspense>
      </Panel>
      <Panel header="Grand Total" bordered shaded className="grand-total-panel" style={{ position: "relative", width: "100%", height: 200 }}>
        <Suspense
          fallback={
            <div data-testid="loader" style={{ width: "100%" }}>
              Loading Chart...
            </div>
          }
        >
          <div style={{ position: "absolute", minHeight: 200, width: "100%", bottom: 0 }}>
            {isEmailsLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.2)",
                  zIndex: 2,
                }}
              >
                <Loader size="lg" content="Loading Chart..." />
              </div>
            )}
            <div style={{ opacity: isGrandTotalsLoading ? 0.3 : 1, height: "100%" }}>
              {!isGrandTotalsLoading && !hasEmailsData ? (
                <div
                  className="canvas-container"
                  style={{ paddingLeft: 20, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 70, fontWeight: "bold" }}
                >
                  ALL DONE
                  </div>
              ) : (
                <div
                  className="canvas-container"
                  style={{ paddingLeft: 20, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <Stat>
                    <HStack spacing={20}>
                      <Stat.Value>${grandTotals?.capturedGrandTotal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Stat.Value>
                      {grandTotals?.entriesGrandTotal && (
                        <Stat.Trend
                          indicator={
                            grandTotals?.capturedGrandTotal === grandTotals?.entriesGrandTotal
                              ? "equal"
                              : grandTotals?.capturedGrandTotal > grandTotals?.entriesGrandTotal
                              ? "up"
                              : "down"
                          }
                        >
                          ${(grandTotals?.capturedGrandTotal - grandTotals?.entriesGrandTotal)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Stat.Trend>
                      )}
                    </HStack>
                  </Stat>
                </div>
              )}
            </div>
          </div>
        </Suspense>
      </Panel>
    </div>
  );
}
export default LandingPage;
